import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCollection, getId, getRoleLabel, getSiteLabel, requestJson, stripEmptyValues } from '../lib/api';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { useMenuPage } from '../hooks/useMenuPage';

const emptyForm = {
  firstName: '',
  lastName: '',
  employeeNumber: '',
  email: '',
  siteId: '',
  siteIds: [],
  roleIds: [],
  logisticsPassword: '',
};

function matchesFilter(value, filter) {
  return String(value || '').toLowerCase().includes(filter.trim().toLowerCase());
}

function getActiveLabel(value) {
  return value === false ? 'Non' : 'Oui';
}

function getUserSiteIds(user) {
  const ids = new Set();
  if (getId(user?.siteId)) ids.add(getId(user.siteId));
  (user?.siteIds || []).forEach((site) => {
    const id = getId(site);
    if (id) ids.add(id);
  });
  return [...ids];
}

function getUserSitesLabel(user) {
  const sites = user?.siteIds?.length ? user.siteIds : user?.siteId ? [user.siteId] : [];
  return sites.map(getUserSiteCodeLabel).filter(Boolean).join(', ');
}

function getUserSiteChips(user) {
  const primaryId = getId(user?.siteId);
  const primaryLabel = getUserSiteCodeLabel(user?.siteId);
  const auxiliarySites = (user?.siteIds || []).filter((site) => getId(site) && getId(site) !== primaryId);
  return {
    primary: primaryLabel ? { id: primaryId || primaryLabel, label: primaryLabel } : null,
    auxiliary: auxiliarySites.map((site) => ({ id: getId(site) || getUserSiteCodeLabel(site), label: getUserSiteCodeLabel(site) })),
  };
}

function getUserSiteCodeLabel(site) {
  if (!site) return '';
  if (typeof site === 'string') return site;
  return site.code || getSiteLabel(site);
}

export default function UsersPage() {
  const [searchParams] = useSearchParams();
  const menuPage = useMenuPage('/utilisateurs');
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBlock, setDeleteBlock] = useState(null);
  const [open, setOpen] = useState(false);
  const [quickFilter, setQuickFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState({
    lastName: '',
    firstName: '',
    fullName: '',
    email: '',
    employeeNumber: '',
    site: '',
    roles: '',
    active: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [error, setError] = useState('');

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const payload = await requestJson('/users?limit=2000');
      setRows(getCollection(payload));
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSites = useCallback(async () => {
    try {
      const payload = await requestJson('/sites');
      setSites(getCollection(payload));
    } catch (_err) {
      setSites([]);
    }
  }, []);

  const loadRoles = useCallback(async () => {
    try {
      const payload = await requestJson('/roles');
      setRoles(getCollection(payload));
    } catch (_err) {
      setRoles([]);
    }
  }, []);

  useEffect(() => {
    loadRows();
    loadSites();
    loadRoles();
  }, [loadRows, loadSites, loadRoles]);

  useEffect(() => {
    const q = searchParams.get('q') || '';
    if (q) setQuickFilter(q);
  }, [searchParams]);

  const gridRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        id: row._id,
        fullName: [row.firstName, row.lastName].filter(Boolean).join(' '),
        employeeNumber: row.employeeNumber || '',
        siteLabel: getUserSiteCodeLabel(row.siteId),
        sitesLabel: getUserSitesLabel(row),
        siteChips: getUserSiteChips(row),
        rolesLabel: (row.roleIds || []).map(getRoleLabel).join(', '),
        activeLabel: getActiveLabel(row.active),
      })),
    [rows]
  );

  const filterOptions = useMemo(() => {
    const collect = (selector) => [...new Set(gridRows.map(selector).filter(Boolean))].sort();

    return {
      lastNames: collect((row) => row.lastName),
      firstNames: collect((row) => row.firstName),
      fullNames: collect((row) => row.fullName),
      emails: collect((row) => row.email),
      employeeNumbers: collect((row) => row.employeeNumber),
      sites: collect((row) => row.sitesLabel),
      roles: collect((row) => row.rolesLabel),
      active: collect((row) => row.activeLabel),
    };
  }, [gridRows]);

  const filteredGridRows = useMemo(() => {
    const normalizedQuickFilter = quickFilter.trim().toLowerCase();

    return gridRows.filter(
      (row) =>
        (!normalizedQuickFilter ||
          [row.lastName, row.firstName, row.fullName, row.employeeNumber, row.email, row.siteLabel, row.sitesLabel, row.rolesLabel, row.activeLabel]
            .join(' ')
            .toLowerCase()
            .includes(normalizedQuickFilter)) &&
        matchesFilter(row.lastName, columnFilters.lastName) &&
        matchesFilter(row.firstName, columnFilters.firstName) &&
        matchesFilter(row.fullName, columnFilters.fullName) &&
        matchesFilter(row.employeeNumber, columnFilters.employeeNumber) &&
        matchesFilter(row.email, columnFilters.email) &&
        matchesFilter(row.sitesLabel, columnFilters.site) &&
        matchesFilter(row.rolesLabel, columnFilters.roles) &&
        matchesFilter(row.activeLabel, columnFilters.active)
    );
  }, [gridRows, quickFilter, columnFilters]);

  const resetFilters = () => {
    setQuickFilter('');
    setColumnFilters({
      lastName: '',
      firstName: '',
      fullName: '',
      email: '',
      employeeNumber: '',
      site: '',
      roles: '',
      active: '',
    });
  };

  const filterFields = [
    ['lastName', 'Nom', filterOptions.lastNames],
    ['firstName', 'Prénom', filterOptions.firstNames],
    ['fullName', 'Collaborateur', filterOptions.fullNames],
    ['employeeNumber', 'Matricule', filterOptions.employeeNumbers],
    ['email', 'Email', filterOptions.emails],
    ['site', 'Site', filterOptions.sites],
    ['roles', 'Rôles', filterOptions.roles],
    ['active', 'Actif', filterOptions.active],
  ];

  const handleOpenCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setOpen(true);
  };

  const handleOpenEdit = useCallback((row) => {
    const primarySiteId = getId(row.siteId);
    setForm({
      firstName: row.firstName || '',
      lastName: row.lastName || '',
      employeeNumber: row.employeeNumber || '',
      email: row.email || '',
      siteId: primarySiteId,
      siteIds: getUserSiteIds(row).filter((siteId) => siteId !== primarySiteId),
      roleIds: (row.roleIds || []).map(getId),
      logisticsPassword: '',
    });
    setEditingId(row._id);
    setOpen(true);
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => {
      if (name === 'siteId') {
        return { ...current, siteId: value, siteIds: (current.siteIds || []).filter((siteId) => siteId !== value) };
      }
      return { ...current, [name]: value };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      setError('');
      const payload = {
        ...form,
        siteIds: [...new Set([form.siteId, ...(form.siteIds || [])].filter(Boolean))],
      };
      await requestJson(editingId ? `/users/${editingId}` : '/users', {
        method: editingId ? 'PUT' : 'POST',
        body: JSON.stringify(stripEmptyValues(payload)),
      });
      setOpen(false);
      await loadRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = useCallback(
    async (id) => {
      try {
        setError('');
        setDeleteBlock(null);
        await requestJson(`/users/${id}`, { method: 'DELETE' });
        setDeleteTarget(null);
        await loadRows();
      } catch (err) {
        if (err.details?.length) {
          setDeleteBlock({
            userId: id,
            userLabel: deleteTarget?.fullName || deleteTarget?.email || '',
            message: err.message,
            details: err.details,
          });
          setDeleteTarget(null);
        }
        setError(err.message);
      }
    },
    [deleteTarget, loadRows]
  );

  const handleDeactivate = useCallback(async () => {
    if (!deleteBlock?.userId) return;
    try {
      setDeactivating(true);
      setError('');
      await requestJson(`/users/${deleteBlock.userId}/deactivate`, { method: 'PATCH' });
      setDeleteBlock(null);
      await loadRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeactivating(false);
    }
  }, [deleteBlock, loadRows]);

  const columns = useMemo(
    () => [
      { field: 'lastName', headerName: 'Nom', minWidth: 160, flex: 0.9 },
      { field: 'firstName', headerName: 'Prénom', minWidth: 150, flex: 0.8 },
      { field: 'employeeNumber', headerName: 'Matricule', minWidth: 120, flex: 0.5 },
      { field: 'fullName', headerName: 'Collaborateur', minWidth: 220, flex: 1 },
      { field: 'email', headerName: 'Email', minWidth: 240, flex: 1 },
      {
        field: 'sitesLabel',
        headerName: 'Sites',
        minWidth: 320,
        flex: 1.35,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center" sx={{ py: 0.75 }}>
            {params.row.siteChips?.primary && (
              <Chip size="small" color="primary" label={params.row.siteChips.primary.label} sx={{ fontWeight: 850 }} />
            )}
            {(params.row.siteChips?.auxiliary || []).map((site) => (
              <Chip key={site.id} size="small" color="secondary" variant="outlined" label={site.label} sx={{ fontWeight: 750 }} />
            ))}
          </Stack>
        ),
      },
      { field: 'rolesLabel', headerName: 'Rôles', minWidth: 260, flex: 1 },
      {
        field: 'active',
        headerName: 'Actif',
        minWidth: 100,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value === false ? 'Non' : 'Oui'}
            color={params.value === false ? 'default' : 'success'}
            variant="outlined"
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Action',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'right',
        headerAlign: 'right',
        minWidth: 120,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ width: '100%' }}>
            <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [handleOpenEdit]
  );

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 850 }}>
          {menuPage.sectionTitle}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>
          {menuPage.pageTitle}
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', borderRadius: 2 }}>
        <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
          <Stack spacing={1.25}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterAltRoundedIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  Filtres
                </Typography>
              </Stack>
              <Button size="small" variant="outlined" onClick={resetFilters} sx={{ minHeight: 32 }}>
                Réinitialiser
              </Button>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(auto-fit, minmax(150px, 1fr))',
                },
                gap: 1.5,
              }}
            >
              <TextField
                size="small"
                label="Recherche globale"
                value={quickFilter}
                onChange={(event) => setQuickFilter(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
              {filterFields.map(([key, label, options]) => (
                <Autocomplete
                  key={key}
                  freeSolo
                  size="small"
                  options={options}
                  value={columnFilters[key] || ''}
                  onInputChange={(_event, value) =>
                    setColumnFilters((current) => ({ ...current, [key]: value || '' }))
                  }
                  onChange={(_event, value) =>
                    setColumnFilters((current) => ({ ...current, [key]: value || '' }))
                  }
                  renderInput={(params) => <TextField {...params} label={label} />}
                  fullWidth
                />
              ))}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {filteredGridRows.length} utilisateur{filteredGridRows.length > 1 ? 's' : ''}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRows}>
              Rafraîchir
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
              Ajouter
            </Button>
          </Stack>
        </Stack>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Box sx={{ height: 620, width: '100%' }}>
            <DataGrid
              rows={filteredGridRows}
              columns={columns}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              sx={{ border: 0 }}
            />
          </Box>
        )}
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle>{editingId ? 'Modifier un utilisateur' : 'Ajouter un utilisateur'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField name="firstName" label="Prénom" value={form.firstName} onChange={handleChange} required fullWidth />
                <TextField name="lastName" label="Nom" value={form.lastName} onChange={handleChange} required fullWidth />
              </Stack>
              <TextField name="employeeNumber" label="Matricule" value={form.employeeNumber} onChange={handleChange} />
              <TextField name="email" label="Email" type="email" value={form.email} onChange={handleChange} required />
              <Autocomplete
                options={sites}
                value={sites.find((site) => site._id === form.siteId) || null}
                onChange={(_event, value) =>
                  setForm((current) => ({
                    ...current,
                    siteId: value?._id || '',
                    siteIds: (current.siteIds || []).filter((siteId) => siteId !== value?._id),
                  }))
                }
                getOptionLabel={getUserSiteCodeLabel}
                renderInput={(params) => <TextField {...params} label="Site principal" required />}
                fullWidth
              />
              <Autocomplete
                multiple
                options={sites.filter((site) => site._id !== form.siteId)}
                value={sites.filter((site) => form.siteIds.includes(site._id))}
                onChange={(_event, values) =>
                  setForm((current) => ({
                    ...current,
                    siteIds: values.map((site) => site._id).filter((siteId) => siteId !== current.siteId),
                  }))
                }
                getOptionLabel={getUserSiteCodeLabel}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option._id} size="small" label={getUserSiteCodeLabel(option)} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sites auxiliaires"
                    helperText="Choix multiple. Le site principal est ajouté automatiquement au périmètre."
                  />
                )}
                fullWidth
              />
              <TextField
                name="roleIds"
                label="Rôles"
                select
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) =>
                    roles
                      .filter((role) => selected.includes(role._id))
                      .map(getRoleLabel)
                      .join(', '),
                }}
                value={form.roleIds}
                onChange={handleChange}
              >
                {roles.map((role) => (
                  <MenuItem key={role._id} value={role._id}>
                    {getRoleLabel(role)}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                <Stack spacing={1.25}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                    Accès Logistique
                  </Typography>
                  <TextField
                    name="logisticsPassword"
                    label="Mot de passe Logistique"
                    type="password"
                    value={form.logisticsPassword}
                    onChange={handleChange}
                    helperText={editingId ? 'Laisse vide pour conserver le mot de passe actuel. L’accès est accordé si le site LOGISTIQUE est rattaché.' : '8 caractères minimum. Rattache aussi le site LOGISTIQUE.'}
                    fullWidth
                  />
                </Stack>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        itemLabel={`l’utilisateur ${deleteTarget?.fullName || deleteTarget?.email || ''}`}
        description={`Confirmer la suppression de l’utilisateur ${
          deleteTarget?.fullName || deleteTarget?.email || ''
        } ?`}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete(deleteTarget._id)}
      />

      <Dialog open={Boolean(deleteBlock)} onClose={() => setDeleteBlock(null)} fullWidth maxWidth="sm">
        <DialogTitle>Suppression impossible</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              {deleteBlock?.message}
              {deleteBlock?.userLabel ? ` (${deleteBlock.userLabel})` : ''}
            </Alert>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {(deleteBlock?.details || []).map((detail) => (
                <Typography key={detail} component="li" variant="body2" color="text.secondary">
                  {detail}
                </Typography>
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Pour conserver les statistiques et l’historique, désactive l’utilisateur au lieu de le supprimer.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteBlock(null)} disabled={deactivating}>
            Fermer
          </Button>
          <Button color="warning" variant="contained" onClick={handleDeactivate} disabled={deactivating}>
            {deactivating ? 'Désactivation…' : 'Désactiver l’utilisateur'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
